import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import CuesheetColumnActions from 'flux/actions/cuesheet/CuesheetColumnActions';
import AlertActions from 'flux/actions/AlertActions';
import ModalActions from 'flux/actions/ModalActions';
import ShowActions from 'flux/actions/ShowActions';
import PopoverActions from 'shared/flux/actions/PopoverActions';
import {
  EditableCellTypes,
  PasteModes,
  KeyScopes,
  FontSizesList,
  FontSizeMap,
  PlaceholderItem,
} from 'flux/stores/cuesheet/CuesheetConstants';
import { OrderedMap, Map, List, fromJS } from 'immutable';
import ItemSetListData from 'shared/data/ItemSetList';
import CuesheetDataHelper from './CuesheetDataHelper';
import ToastActions from 'flux/actions/ToastActions';
import ActivityAuditListActions from 'flux/actions/ActivityAuditListActions';
import moment from 'moment';
import _ from 'underscore';
import Rx from 'rxjs/Rx';
import UIRegions from 'shared/constants/UIRegions';
import MessageConstants from 'shared/constants/Messages';
import keyBinder from 'shared/lib/keyBinder';
import TimeUtil from 'time';
import ConfirmActions from 'flux/actions/ConfirmActions';
import { convertFromRaw } from 'shared/lib/draft';
import { Modifier, SelectionState, EditorState, convertToRaw } from 'draft-js';
import {
  getPlainTextFromRawRTEState,
  getDefaultRawRTEState,
} from '@shoflo-rundown/draft-js-utils';

import {
  addLinkToRTEState,
  addImageToRTEState,
  addTextToRTEState,
  appendTextToRTEState,
  getDefaultRTEState,
  clearRTEStyles,
} from 'shared/lib/rteHelper';
import { isCompact } from 'main/components/show/cuesheet/cuesheetViewHelper';
import { persistCopyState } from 'flux/stores/copyPersistence';
import readOnlyHelper from 'flux/helpers/readOnlyHelper';
import { imageHeightCache } from 'shared/flux/helpers/preloadItemSetImages';

/**
 * CuesheetCell Mixin
 */
export default {
  init() {
    /**
     * Set up observable subjects
     */
    this.cellObservables = {};
    this.cellObservables.saveMultipleDataCellChangesBuffer = new Rx.Subject();
    this.cellObservables.saveItemFieldChangesDebounce = new Rx.Subject();

    /**
     * Subscriber function for observables...save cell changes transient
     */
    const saveMultipleDataCellChangesTransient = ({
      next,
      rowIdsCacheBust,
    }) => {
      this.setStateAndTrigger(
        next
          .delete('scrollRowIndex')
          .delete('scrollColumnIndex')
          .delete('scrollRowWithOffsetIndex'),
      );

      // Add delay before recalculating row heights to allow images to load
      setTimeout(() => {
        CuesheetRowActions.bustRowHeightCache(rowIdsCacheBust);
      }, 500);
    };

    /**
     * Subscriber function for observables...save cell changes persistent
     */
    const saveMultipleDataCellChangesPersistent = (requests) => {
      const groups = {};
      //We group the cell changes by showId because it is required by by the api call.
      //This is only used when cutting from one show and pasting into another
      requests.forEach((request) => {
        const showId = request.params.get('showId');
        if (!groups[showId]) groups[showId] = [];
        groups[showId].push(request);
      });

      Object.keys(groups).forEach((key) => {
        // All items in a group should have the same params so it shouldn't matter which item
        // we pull the params from. Here we're using the final item in the group.
        const { params } = groups[key].slice(-1)[0];

        const changesArray = [];

        groups[key].forEach((group) => {
          changesArray.push(group.changes);
        });

        let mergedChanges = OrderedMap({});

        changesArray.forEach((change) => {
          change.forEach((row, rowId) => {
            // We loop through all the row id's in a set of changes. If a row does not
            // exist in the mergedChanges object, we add it.
            if (!mergedChanges.has(rowId)) {
              mergedChanges = mergedChanges.set(rowId, OrderedMap());
            }
            // We loop through each cell in each row. If the newest group of changes has a matching
            // row and cell id, we overwrite the old edit with the most recent edit
            change.get(rowId).forEach((column, columnId) => {
              mergedChanges = mergedChanges.setIn([rowId, columnId], column);
            });
          });
        });

        CuesheetCellActions.saveMultipleDataCellChangesPersistent(
          params.get('teamId'),
          params.get('eventId'),
          params.get('showId'),
          params.get('itemSetListId'),
          params.get('itemSetId'),
          mergedChanges.toJS(),
        );
      });
    };

    /**
     * Subscriber function for observables...save item field changes transient
     */
    const saveItemFieldChangesTransient = ({ rowIndex, changes, state }) => {
      const next = this.saveItemFieldChangesTransient(rowIndex, changes, state);

      this.setStateAndTrigger(next);
    };

    /**
     * Subscriber function for observables...save item field changes changes persistent
     */
    const saveItemFieldChangesPersistent = ({
      itemSetId,
      rowId,
      changes,
      state,
    }) => {
      this._saveItemFieldChangesPersistent(itemSetId, rowId, changes, state);
    };

    this.cellObservables.saveMultipleDataCellChangesBuffer.subscribe(
      saveMultipleDataCellChangesTransient,
    );
    this.cellObservables.saveMultipleDataCellChangesBuffer
      .filter((item) => item.persistent)
      .bufferTime(1000)
      .filter((items) => items.length)
      .subscribe(saveMultipleDataCellChangesPersistent);

    this.cellObservables.saveItemFieldChangesDebounce.subscribe(
      saveItemFieldChangesTransient,
    );
    this.cellObservables.saveItemFieldChangesDebounce
      .map(({ rowIndex, changes, state }) => {
        const rowSpec = this.getInState(['rows', rowIndex]),
          rowPath = rowSpec.get('path');

        const itemSetId = rowPath[0],
          rowId = rowPath[2];

        return { itemSetId, rowId, changes, state };
      })
      .subscribe(saveItemFieldChangesPersistent);
  },

  onCancelEditing() {
    this.setStateAndTrigger(this.clearRowFieldState('editing'));
  },

  onRevertToLastSuccessfulCellState() {
    const state = this.getState();

    const lastSuccessfulCellState = state.get('lastSuccessfulCellState');

    const rowId = lastSuccessfulCellState.get('rowId');
    const cellSpec = lastSuccessfulCellState.get('cellSpec');
    const rteState = lastSuccessfulCellState.get('rteState');

    const next = this._getTransientRTEStateByRowId(
      rowId,
      cellSpec,
      rteState,
      state,
    );

    this.setStateAndTrigger(next);
  },

  onSaveFeaturedZoom(featuredZoom) {
    let next = this.getState();

    next = next.setIn(['userSettings', 'featuredZoom'], featuredZoom);

    CuesheetColumnActions.updateUserSettingsPersistent(
      next.get('teamId'),
      next.get('eventId'),
      next.get('showId'),
      next.get('itemSetListId'),
      next.get('userId'),
      { featuredZoom },
    );

    this.setStateAndTrigger(next);
  },

  onSaveSelectedCellFormat(formatChange, removeStyle) {
    let next = this.getState();

    let persistentChanges = OrderedMap();

    // const rowIdsCacheBust = [];

    const itemSetId = next.get('rowMap').keySeq().first();

    if (next.get('rowSelectionMode')) {
      /**
       * Handle row selection formatting, which takes precedence over cell selections
       */

      const columnIds = next.get('columnOrder');

      persistentChanges = persistentChanges.withMutations((changes) => {
        this.getInState('rows').forEach((row, indx) => {
          if (!row.get('rowSelected')) return;
          const rowId = this.findRowId(indx);
          const mutation = Map({
            formatChange,
            userId: this.getInState('userId'),
          });
          columnIds
            .concat(['ItemTitle', 'ItemDescription', 'ItemNote'])
            .forEach((columnId) => changes.setIn([rowId, columnId], mutation));
        });
      });
    } else {
      /**
       * Handle the case where cells are selected
       */

      //1. Get the selected cell
      const selectedCell = this.getSelectedCell();
      const selectedCells = next.get('selectedCells');

      //2. If no selected cell, return
      if (!selectedCell) return;

      selectedCells.forEach((cell) => {
        const cellFormat = this.getCellFormat(cell);

        //Instead of only saving the additional format changes individually, we merge them with the
        //existing format and save. This allows us to keep track of formatting changes in the activity
        //feed

        let newFormat = formatChange
          ? cellFormat.merge(formatChange)
          : cellFormat;

        if (removeStyle && newFormat.has(removeStyle))
          newFormat = newFormat.delete(removeStyle);

        const spec = cell.cellSpec;
        const { rowId } = cell;
        const rowIndex = this.findRowPosition(itemSetId, rowId);

        //Return if row is locked
        if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

        // Value property is used to determine the columnId of a regular data cells
        let columnId = spec.get('value');
        const columnType = spec.get('type');

        let rteStateField = 'rteState';
        let rteState;

        const rowSpec = this.getInState(['rows', rowIndex]),
          rowPath = rowSpec.get('path');

        if (
          [
            EditableCellTypes.ItemTitle,
            EditableCellTypes.ItemDescription,
          ].indexOf(columnType) !== -1
        ) {
          columnId = columnType;

          if (columnType === EditableCellTypes.ItemTitle) {
            rteStateField = 'rteTitleState';
          } else {
            rteStateField = 'rteDescriptionState';
          }

          rteState = this.getInState(['rowMap'].concat(rowPath, rteStateField));
        } else if (EditableCellTypes.ItemNote === columnType) {
          columnId = columnType;
          persistentChanges = persistentChanges.setIn(
            [rowId, columnId, 'userId'],
            this.getInState('userId'),
          );

          rteState = this.getInState(
            ['rowMap'].concat(
              rowPath,
              'userSettings',
              this.getInState('userId'),
              'note',
              'rteState',
            ),
          );
        } else {
          rteState = this.getInState(
            ['rowMap'].concat(rowPath, 'cells', columnId, 'rteState'),
          );
        }

        if (!rteState) rteState = getDefaultRTEState();

        if (!formatChange) {
          rteState = clearRTEStyles(rteState);
          persistentChanges = persistentChanges.setIn(
            [rowId, columnId, rteStateField],
            rteState,
          );
          persistentChanges = persistentChanges.setIn(
            [rowId, columnId, 'format'],
            Map({}),
          );
        }

        if (formatChange) {
          const removals = { [removeStyle]: removeStyle };
          rteState = _applyCellFormatsToRTEState(
            cellFormat,
            formatChange,
            Map(removals),
            rteState,
          );
          persistentChanges = persistentChanges.setIn(
            [rowId, columnId, rteStateField],
            rteState,
          );
          persistentChanges = persistentChanges.setIn(
            [rowId, columnId, 'format'],
            newFormat,
          );
        }
      });
    }

    if (persistentChanges.size)
      CuesheetCellActions.saveMultipleDataCellChanges(persistentChanges);
  },

  /**
   * Save Selected Cell Text and New Time Stamp
   */
  onSaveSelectedCellTextWithTimeStamp() {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { rowIndex, cellSpec } = selectedCell;

    const rteState = this._getRTEStateOfSelectedCell().update((rteState) => {
      if (
        rteState.get('blocks').size === 1 &&
        rteState.getIn(['blocks', 0, 'text']) === ''
      ) {
        return appendTextToRTEState(rteState, this.getTimeStamp());
      }
      return addTextToRTEState(rteState, this.getTimeStamp());
    });

    const changes = this._saveRTEState(rowIndex, cellSpec, rteState);

    this.saveMultipleDataCellChanges(changes, true, false);
  },

  getTimeStamp() {
    const twentyFourHour = this.getInState(['event', 'twentyFourHourClock']);
    const timeZone = this.getInState(['event', 'timeZone']);

    const timeStamp =
      moment.tz(timeZone).format('MM/DD/YY') +
      ' ' +
      TimeUtil.getFormattedTimeWithSeconds(moment(), twentyFourHour, timeZone);

    return timeStamp;
  },

  /**
   * Save Selected Cell Text and New Link
   *
   * @param linkText
   * @param linkUrl
   */
  onSaveSelectedCellTextWithLink(linkText, linkUrl) {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { rowIndex, cellSpec } = selectedCell;

    const rteState = this._getRTEStateOfSelectedCell().update((rteState) => {
      return addLinkToRTEState(rteState, linkText, linkUrl);
    });

    const changes = this._saveRTEState(rowIndex, cellSpec, rteState);

    this.saveMultipleDataCellChanges(changes, true, false);
  },

  onSaveSelectedCellTextWithImage(imgTitle, imgUrl) {
    this._saveSelectedCellTextWithImage(imgTitle, imgUrl);
  },

  onAddCellAsset(title, file) {
    let state = this._getClearedEditState();
    state = this.clearPastableCellsBuffer(state);
    CuesheetCellActions.uploadCellAsset(
      state.get('teamId'),
      state.get('eventId'),
      title,
      file,
    );
  },

  onUploadCellAssetCompleted(imgTitle, imgUrl, s3Key) {
    this._saveSelectedCellTextWithImage(imgTitle, imgUrl, s3Key);
  },

  /**
   * Save Selected Cell Text and New Image
   *
   * @param imgTitle
   * @param imgUrl
   */
  _saveSelectedCellTextWithImage(imgTitle, imgUrl, s3Key) {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { rowIndex, cellSpec } = selectedCell;

    const rteState = this._getRTEStateOfSelectedCell().update((rteState) => {
      return addImageToRTEState(rteState, imgTitle, imgUrl, s3Key);
    });

    const imgPreload = new Image();
    imgPreload.src = imgUrl;
    imgPreload.onload = () => {
      const imgKey = s3Key || imgUrl;
      imageHeightCache[imgKey] = {
        naturalHeight: imgPreload.naturalHeight,
        naturalWidth: imgPreload.naturalWidth,
      };
      CuesheetRowActions.bustRowHeightCache(cellSpec.get('rowId'));

      const changes = this._saveRTEState(rowIndex, cellSpec, rteState);

      this.saveMultipleDataCellChanges(changes, true, false);
    };
  },

  /**
   * Get rteState based on cell type in cellSpec.
   *
   * @param cellSpec
   */
  _getRTEStateOfSelectedCell() {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return getDefaultRTEState();

    const { cellSpec } = selectedCell;

    let rteState = getDefaultRTEState();

    if (cellSpec.get('type') === EditableCellTypes.Data) {
      rteState = this.getSelectedDataCellState();
    } else if (cellSpec.get('type') === EditableCellTypes.ItemNote) {
      rteState = this.getSelectedItemNoteState();
    } else if (cellSpec.get('type') === EditableCellTypes.ItemTitle) {
      rteState = this.getSelectedItemFieldState('rteTitleState');
    } else if (cellSpec.get('type') === EditableCellTypes.ItemDescription) {
      rteState = this.getSelectedItemFieldState('rteDescriptionState');
    }

    return rteState || Map(getDefaultRawRTEState());
  },

  /**
   * Save modified rteState
   *
   * @param rowIndex
   * @param cellSpec
   * @param rteState - rteState that gets saved
   */
  _saveRTEState(rowIndex, cellSpec, rteState) {
    //Check if selected cell row is locked
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    const state = this.getState();

    const rowSpec = this.getInState(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      rowId = rowPath[2];

    let cellChanges = Map({});
    const cellType = cellSpec.get('type');
    const colId =
      cellType === EditableCellTypes.Data ? cellSpec.get('value') : cellType;

    cellChanges = cellChanges
      .set('userId', state.get('userId'))
      .set('rteState', rteState);

    if (cellSpec.get('type') === EditableCellTypes.ItemTitle) {
      cellChanges = cellChanges.set('rteTitleState', rteState);
    }

    if (cellSpec.get('type') === EditableCellTypes.ItemDescription) {
      cellChanges = cellChanges.set('rteDescriptionState', rteState);
    }

    let changes = OrderedMap();

    changes = changes.withMutations((data) => {
      data.set(rowId, OrderedMap());
      data.setIn([rowId, colId], cellChanges);
    });

    return changes;
  },

  _getTransientRTEStateByRowId(rowId, cellSpec, rteState, state) {
    state = state || this.getState();

    const itemSetId = state.get('rowMap').keySeq().first();
    const rowIndex = this.findRowPosition(itemSetId, rowId, state);
    return this._getTransientRTEState(rowIndex, cellSpec, rteState, state);
  },

  /**
   * Set transient rteState
   *
   * @param rowIndex
   * @param cellSpec
   * @param rteState
   */
  _getTransientRTEState(rowIndex, cellSpec, rteState, state) {
    let next = state || this.getState();

    //Check if selected cell row is locked
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return next;

    //Determine the type of cell, and call the appropriate action
    if (cellSpec.get('type') === EditableCellTypes.Data) {
      next = this.saveDataCellChangesTransient(
        rowIndex,
        cellSpec.get('value'),
        Map({ rteState }),
        state,
      );
    } else if (cellSpec.get('type') === EditableCellTypes.ItemNote) {
      next = this.saveNoteCellChangesTransient(
        rowIndex,
        next.get('userId'),
        Map({ rteState }),
        state,
      );
    } else if (cellSpec.get('type') === EditableCellTypes.ItemTitle) {
      next = this.saveItemFieldChangesTransient(
        rowIndex,
        Map({ rteTitleState: rteState }),
        state,
      );
    } else if (cellSpec.get('type') === EditableCellTypes.ItemDescription) {
      next = this.saveItemFieldChangesTransient(
        rowIndex,
        Map({ rteDescriptionState: rteState }),
        state,
      );
    }

    return next;
  },

  /**
   * Used when the cell is not selected and rowIndex is not present (e.g. in the activity audit feed use-case)
   */
  onSaveDataCellTextWithIds(
    itemSetId,
    rowId,
    columnId,
    rteState,
    cellSpec,
    cellFormat,
  ) {
    CuesheetCellActions.saveDataCellChanges(
      rowId,
      cellSpec,
      rteState,
      cellFormat,
    );
  },

  /**
   * Used when the field is not selected, or it's being saved from outside the cuesheet index
   * when a rowIndex is not available (e.g. activity audit feed)
   */
  onSaveItemFieldWithIds(itemSetId, rowId, cellSpec, rteState) {
    CuesheetCellActions.saveDataCellChanges(rowId, cellSpec, rteState);
  },

  onShowCuesheetModal(type) {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { rowIndex } = selectedCell;

    //Check if selected cell row is locked
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    ModalActions.show(type);
  },

  /**
   * Delete the text of the currently selected cell
   */
  onClearSelectedCell() {
    if (!this.isEventContributor()) return;

    //1. Get the selected cell
    const selectedCell = this.getSelectedCell();

    //2. If no selected cell, check if there's a selected header cell
    if (!selectedCell) {
      const selectedColumn = this._getSelectedColumn();

      if (selectedColumn) {
        CuesheetCellActions.saveHeaderCellChanges(
          selectedColumn.get('_id'),
          '',
        );
      }
      return;
    }

    const { rowIndex, cellSpec } = selectedCell;

    const type = cellSpec.get('type');

    //Check if row is locked
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    /**
     * If user does not have admin level permission then return and do nothing
     */
    if (
      (type === EditableCellTypes.ItemTitle ||
        type === EditableCellTypes.ItemDescription) &&
      !this.isEventAdmin()
    )
      return;

    //Determine the type of cell, and call the appropriate action
    if (this.getInState('selectedCells').size > 1) {
      if (
        type === EditableCellTypes.ItemStartTime ||
        type === EditableCellTypes.ItemPresetDuration ||
        type === EditableCellTypes.ItemDuration
      ) {
        ConfirmActions.prompt(
          `This will bulk hide the times in the ${
            this.getInState('selectedCells').size
          } cells you have selected.
          Heads up, you can not undo a bulk action like this. You will still be able to
          undo this action from individual cells.`,
          () => this._toggleTimeCellsVisibility(false),
        );
      } else {
        ConfirmActions.prompt(
          `This will bulk delete the ${
            this.getInState('selectedCells').size
          } cells you have selected.
          Heads up, you can not undo a bulk action like this. You will still be able to
          undo this action from individual cells.`,
          () => this._clearAllSelectedCells(),
        );
      }
    } else if (
      type === EditableCellTypes.ItemStartTime ||
      type === EditableCellTypes.ItemPresetDuration ||
      type === EditableCellTypes.ItemDuration
    ) {
      this._toggleTimeCellsVisibility(false);
    } else {
      this._clearAllSelectedCells();
    }

    const columnType = cellSpec.get('value')
      ? cellSpec.get('value')
      : cellSpec.get('type');
    const corruptedCellPath = [
      'corruptedCells',
      `${selectedCell.rowId}_${columnType}`,
    ];

    /**
     * Remove corrupted flag for a cell when rteState is cleared.
     */
    if (this.getInState(corruptedCellPath)) {
      this.deleteInStateAndTrigger(corruptedCellPath);
    }
  },

  /**
   * This is used to clear data from all selected data cells.
   *
   * Why?
   * This is used for deleting text/format from all selected cells at one time.
   *
   */
  _clearAllSelectedCells() {
    let next = this.getState();

    const itemSetId = next.get('rowMap').keySeq().first();

    let lockedRowFound = false,
      noPermission = false;

    next.get('selectedCells').forEach((selectedCell) => {
      const rowIndex = this.findRowPosition(itemSetId, selectedCell.rowId);
      //Check for locked row
      if (this.getItemRowLockStatus(rowIndex)) lockedRowFound = true;

      const isItemOrDesc =
        [
          EditableCellTypes.ItemDescription,
          EditableCellTypes.ItemTitle,
        ].indexOf(selectedCell.cellSpec.get('type')) >= 0;
      if (isItemOrDesc && !this.isEventAdmin()) noPermission = true;
    });

    /**
     * Return and throw alert if locked row is found amoung selections
     */
    if (lockedRowFound)
      return AlertActions.show(
        MessageConstants.lockedRowModal,
        UIRegions.Cuesheet,
      );
    if (noPermission)
      return AlertActions.show(
        MessageConstants.lockedRowModal,
        UIRegions.Cuesheet,
      );

    /**
     * These two functions amount to clearing/deleting
     */
    next = this._addSelectedCellsToCutBuffer(next);

    this.setStateAndTrigger(next);

    this._cutCellData();
  },

  onShowCellHistory(rowIndex, cellSpec) {
    this._showCellHistory(rowIndex, cellSpec);
  },

  /**
   * Show the audit feed for the selected cell (only title, description, and data cell types)
   */
  onShowSelectedCellHistory() {
    //1. Get the selected cell
    const selectedCell = this.getSelectedCell();

    //2. If no selected cell, return
    if (!selectedCell) return;

    const { rowIndex, cellSpec } = selectedCell;

    this._showCellHistory(rowIndex, cellSpec);
  },

  _showCellHistory(rowIndex, cellSpec) {
    const type = cellSpec.get('type');
    const path = this.getInState(['rows', rowIndex, 'path']);
    const rowId = path[2],
      columnId = cellSpec.get('value');

    //3. If data cell, call the action to populate the activity list w/ cell data
    if (type === EditableCellTypes.Data) {
      ActivityAuditListActions.populateForItemCell(
        this.getInState('teamId'),
        this.getInState('eventId'),
        this.getInState('showId'),
        rowId,
        columnId,
        1,
      );
    } else if (
      //4. If title or description, call the action to populate the activity list w/ cell data
      type === EditableCellTypes.ItemTitle ||
      type === EditableCellTypes.ItemDescription
    ) {
      const fieldName =
        type === EditableCellTypes.ItemTitle ? 'title' : 'description';

      ActivityAuditListActions.populateForItemField(
        this.getInState('teamId'),
        this.getInState('eventId'),
        this.getInState('showId'),
        rowId,
        fieldName,
        1,
      );
    }
  },

  /**
   * Switch into edit mode for the currently selected cell
   *
   */
  // eslint-disable-next-line no-unused-vars
  onStartEditingSelectedCell(e, firstEdit) {
    const selectedCell = this.getSelectedCell();

    if (readOnlyHelper.alertPastEventNotEditable()) {
      return;
    }

    if (!selectedCell) {
      const selectedColumn = this._getSelectedColumn();

      if (selectedColumn) {
        this._startEditingHeaderCell(selectedColumn.get('_id'));
      }

      return;
    }

    let state = this.getState();

    const { rowIndex, cellSpec } = selectedCell;

    const cellType = selectedCell ? selectedCell.cellSpec.get('type') : null;

    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    if (!this._hasCellPermission(cellType)) return;
    state = this._startEditingRow(rowIndex, cellSpec, state);

    //Don't allow editing of corrupted rows
    if (this._rowIsCorrupted(selectedCell.rowIndex)) return;

    if (
      cellType === EditableCellTypes.ItemStartTime ||
      cellType === EditableCellTypes.ItemPresetDuration ||
      cellType === EditableCellTypes.ItemDuration
    ) {
      if (this.getInState('selectedCells').size > 1) {
        ConfirmActions.prompt(
          `This will bulk reveal the times in the ${
            this.getInState('selectedCells').size
          } cells you have selected.
          Heads up, you can not directly undo a bulk action like this.`,
          () => this._toggleTimeCellsVisibility(true),
        );
      } else {
        PopoverActions.show(cellType + selectedCell.rowIndex);
        return;
      }
    }

    let nextCharacter;

    /**
     * When a cell goes into edit mode, we want to capture the key they pressed and put it into RTE state..
     */
    if (e) {
      if (e.key) {
        nextCharacter = e.key;
      } else if (e.keyCode) {
        //Because at least some versions of Safari don't support `.key`
        //https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
        nextCharacter = String.fromCharCode(e.keyCode).toLowerCase();
        if (e.shiftKey) {
          nextCharacter = nextCharacter.toUpperCase();
        }
      }
    }

    const initialRTEState = this._getRTEStateOfSelectedCell();

    if (nextCharacter) {
      const nextRTEState = initialRTEState.update((rteState) => {
        return appendTextToRTEState(rteState, nextCharacter);
      });

      state = this._getTransientRTEState(
        rowIndex,
        cellSpec,
        nextRTEState,
        state,
      );

      this.setStateAndTrigger(state);
    }

    const lastSuccessfulCellState = Map({
      rowId: this.findRowId(rowIndex, state),
      cellSpec: selectedCell.cellSpec,
      rteState: initialRTEState,
      text: getPlainTextFromRawRTEState(initialRTEState.toJS()),
    });

    this.setInState('lastSuccessfulCellState', lastSuccessfulCellState);
  },

  onRefocusCell() {
    const selectedCell = this.getSelectedCell();

    const { rowIndex, cellSpec } = selectedCell;

    const nextState = this.getState()
      .set('scrollColumnIndex', this._getCellIndex(Map(cellSpec)) + 1)
      .set('scrollRowIndex', rowIndex);

    this.setStateAndTrigger(nextState);
  },

  /**
   * Move the cursor to an item text field cell
   */
  onSelectItemCell(rowIndex, type) {
    if (!this._itemCellIsEditable(rowIndex, type)) return;

    this._clearColumnHeaderSelectedCellSelection();

    this._setToolbarVisibility(type);

    this.setCursor(rowIndex, { type }, true);
  },

  /**
   * Start editing an item text field cell
   */
  onStartEditingItemCell(rowIndex, type) {
    //Don't allow editing of corrupted rows
    if (this._rowIsCorrupted(rowIndex)) return;

    if (!this._hasCellPermission(type)) return;

    if (!this._itemCellIsEditable(rowIndex, type)) return;

    if (readOnlyHelper.alertPastEventNotEditable()) {
      return;
    }

    this._startEditingRow(rowIndex, { type });

    const cellSpec = Map({ type });

    const initialRTEState = this._getRTEStateOfSelectedCell();

    this.setInState(
      'lastSuccessfulCellState',
      Map({
        rowId: this.findRowId(rowIndex),
        cellSpec,
        rteState: initialRTEState,
        text: getPlainTextFromRawRTEState(initialRTEState.toJS()),
      }),
    );
  },

  /**
   * Move the cursor to an item text field cell
   */
  onSelectTimeCell(rowIndex, type) {
    this._clearColumnHeaderSelectedCellSelection();

    this._setToolbarVisibility(type);

    this.setCursor(rowIndex, { type }, true);
  },

  _itemCellIsEditable(rowIndex, type) {
    //If the show is in autoCount mode, and the row index is not zero (the first), don't allow selection
    return !(
      type === EditableCellTypes.ItemNumber &&
      this.getInState(['show', 'autoCountCues']) &&
      rowIndex !== 0
    );
  },

  /**
   * Select a header cell
   */
  onSelectHeaderCell(columnId) {
    this._selectHeaderCell(columnId);
  },

  _selectHeaderCell(columnId) {
    const next = this.getClearedCursorState().setIn(
      ['columns', columnId, 'selected'],
      true,
    );
    this.setStateAndTrigger(next);
  },

  /**
   * Start editing a header cell
   */
  onStartEditingHeaderCell(columnId) {
    if (!this.isEventAdmin()) return;

    if (readOnlyHelper.alertPastEventNotEditable()) {
      return;
    }

    this._startEditingHeaderCell(columnId);
  },

  /**
   * Save header cell changes
   */
  onSaveHeaderCellChanges(columnId, text) {
    let next = this._getClearedEditState()
      .setIn(['columns', columnId, 'title'], text)
      .setIn(['fullColumns', columnId, 'title'], text);

    if (next.hasIn(['columnsGuestPass', columnId])) {
      next = next.setIn(['columnsGuestPass', columnId, 'title'], text);
    }

    CuesheetActions.updateColumnPersistent(
      next.get('teamId'),
      next.get('eventId'),
      next.get('showId'),
      next.get('itemSetListId'),
      columnId,
      { title: text },
    );

    this.setStateAndTrigger(next);
  },

  _clearColumnHeaderSelectedCellSelection() {
    if (!this._isHeaderCellSelected()) return;

    const next = this.getClearedCursorState().setIn(
      ['columns', this._getSelectedColumn().get('_id'), 'selected'],
      false,
    );

    this.setState(next);
  },

  onSelectFeaturedCell() {
    const columnId = this.getInState(['userSettings', 'featuredColumn']),
      rowIndex = this.getInState('featuredCellRowIndex');

    this.setInState('cellToolbarEnabled', false);

    this._clearColumnHeaderSelectedCellSelection();
    this.setCursor(
      rowIndex,
      { type: EditableCellTypes.Data, value: columnId },
      true,
    );
  },

  onStartEditingFeaturedCell() {
    const columnId = this.getInState(['userSettings', 'featuredColumn']),
      rowIndex = this.getInState('featuredCellRowIndex');

    //Don't allow editing of corrupted rows
    if (this._rowIsCorrupted(rowIndex)) return;

    if (readOnlyHelper.alertPastEventNotEditable()) {
      return;
    }

    CuesheetCellActions.startEditingDataCell(rowIndex, columnId);
  },

  /**
   * Select a data (column) cell (set the cursor to it)
   */
  onSelectDataCell(rowIndex, columnId) {
    this._setToolbarVisibility(EditableCellTypes.Data);

    this._clearColumnHeaderSelectedCellSelection();

    this.setCursor(
      rowIndex,
      { type: EditableCellTypes.Data, value: columnId },
      true,
    );
  },

  /**
   * Start editing a data (column) cell (set the cursor to it)
   */
  onStartEditingDataCell(rowIndex, columnId) {
    //Don't allow editing of corrupted rows
    if (this._rowIsCorrupted(rowIndex)) return;

    if (!this.isEventContributor()) return;

    if (readOnlyHelper.alertPastEventNotEditable()) {
      return;
    }

    const cellSpec = Map({ type: EditableCellTypes.Data, value: columnId });

    /**
     * When holding down the shift key the user should not be able to go into
     * edit mode.
     */
    if (keyBinder.shift) return this.setCursor(rowIndex, cellSpec, true);

    if (this.getInState('selectedCells').size > 1) {
      this.deselectAllCells();

      return this.setCursor(rowIndex, cellSpec, true);
    }

    this._startEditingRow(rowIndex, cellSpec);

    const initialRTEState = this._getRTEStateOfSelectedCell();

    this.setInState(
      'lastSuccessfulCellState',
      Map({
        rowId: this.findRowId(rowIndex),
        cellSpec,
        rteState: initialRTEState,
        text: getPlainTextFromRawRTEState(initialRTEState.toJS()),
      }),
    );
  },

  /**
   * Convert single cell changes to multi-cell
   * Save a item/description/note/ or data (column) cells
   * rteState MUST be deeply converted to immutable ie fromJS instead of Map on nested rteState
   * @param rowIndex - num
   * @param cellSpec - Immutable Map
   * @param rteState - Immutable Map
   */
  onSaveDataCellChanges(rowId, cellSpec, rteState, cellFormat) {
    const itemSetId = this.getInState('rowMap').keySeq().first();

    if (
      this.getEditableCellStatusInLockedRowWithAlert(
        this.findRowPosition(itemSetId, rowId, this.getState()),
        cellSpec,
      )
    )
      return;

    let state = this._getClearedEditState();

    const colId = cellSpec.get('value');

    const lastSuccessfulCellState = state.get('lastSuccessfulCellState');

    const hasChanged = _rteHasChanged(lastSuccessfulCellState, rteState);

    if (!hasChanged) {
      const rowIndex = this.findRowPosition(itemSetId, rowId, state);

      //make sure transient state matches persistent
      state = this._getTransientRTEState(
        rowIndex,
        cellSpec,
        lastSuccessfulCellState.get('rteState'),
        state,
      );

      return this.setStateAndTrigger(state);
    }

    let cellChanges = Map({});
    const cellType = cellSpec.get('type');

    const format = cellFormat || this.getCellFormat({ rowId, cellSpec });

    if (format) cellChanges = cellChanges.set('format', format);

    cellChanges = cellChanges.set('userId', state.get('userId'));
    cellChanges = cellChanges.set('rteState', rteState);

    if (cellType === EditableCellTypes.ItemTitle)
      cellChanges = cellChanges.set('rteTitleState', rteState);
    if (cellType === EditableCellTypes.ItemDescription)
      cellChanges = cellChanges.set('rteDescriptionState', rteState);

    let changes = OrderedMap();

    changes = changes.withMutations((data) => {
      data.set(rowId, OrderedMap());
      data.setIn([rowId, colId], cellChanges);
    });

    this.saveMultipleDataCellChanges(changes, true, false);
  },

  onSaveMultipleDataCellChanges(changes) {
    this.saveMultipleDataCellChanges(changes, true, false);
  },

  /**
   * Get cell format
   */
  _getCellFormat() {
    const format = Map();

    const selectedCells = this.getInState('selectedCells');

    if (!selectedCells || !selectedCells.size) return format;

    return this.getCellFormat(selectedCells.first()) || Map();
  },

  onToggleTextSizeGrow() {
    this._saveFormatFontSize('grow');
  },

  onToggleTextSizeShrink() {
    this._saveFormatFontSize('shrink');
  },

  onToggleCellForeColor() {
    this._saveFormatColor('foreColor');
  },

  onToggleCellBackColor() {
    this._saveFormatColor('backColor');
  },

  _getNoteCellPath(rowIndex, userId) {
    const state = this.getState();
    const id = userId || state.get('userId');

    const rowSpec = this.getInState(['rows', rowIndex]),
      rowPath = rowSpec.get('path');

    return ['rowMap'].concat(rowPath, 'userSettings', id, 'note');
  },

  saveNoteCellChangesTransient(rowIndex, userId, changes, initState) {
    const state = initState || this.getState();

    const cellPath = this._getNoteCellPath(rowIndex, userId);

    //Update the cell in next state
    const next = state
      .updateIn(cellPath, (cell) => {
        return _mergeCellChangesTransient(
          cell,
          changes,
          EditableCellTypes.ItemNote,
        );
      })
      //'touch' the row to force an update of the row
      .setIn(['rows', rowIndex, 'touched'], new Date().getTime());

    CuesheetRowActions.bustRowHeightCache(cellPath[3]);

    return next;
  },

  /**
   * Save an item field cell
   */
  onSaveItemFieldChanges(rowIndex, field, text) {
    const changes = {};
    changes[field] = text;

    // Clear the edit state
    const state = this._getClearedEditState();

    this.cellObservables.saveItemFieldChangesDebounce.next({
      rowIndex,
      changes,
      state,
    });
  },

  onSaveItemTimeFields(rowIndex, changes) {
    changes = Map(changes);

    // we want to allow changes to actualDurationInSeconds to apply even when a row is locked
    if (changes.has('actualDurationInSeconds') && changes.size === 1) {
      return this._saveItemTimeFields(rowIndex, changes);
    }

    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    this._saveItemTimeFields(rowIndex, changes);
  },

  onSaveItemTimeField(rowIndex, field, value) {
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    //If first row start time get's updated, also update show start time
    if (typeof rowIndex === 'number' && rowIndex === 0 && field === 'startOn') {
      const updateObj = { [field]: value };
      ShowActions.save(
        this.getInState('teamId'),
        this.getInState('eventId'),
        this.getInState('showId'),
        updateObj,
        this.getInState(['show', 'displayType']),
      );
    }

    const changes = Map().set(field, value);

    this._saveItemTimeFields(rowIndex, changes);
  },

  _saveFormatFontSize(type) {
    const sizes = FontSizesList;

    const currentFormat = this._getCellFormat(); // ex: {fontSize: "large"}

    const fontFormat = currentFormat.get('fontSize');
    const curFontSize = fontFormat ? fontFormat : 'normal';
    let indx = sizes.indexOf(curFontSize);

    if (type === 'shrink') {
      indx -= 1;
      if (indx < 0) return;
    }

    if (type === 'grow') {
      indx += 1;
      if (indx === sizes.length) return;
    }

    const formatChange = Map({ fontSize: sizes[indx] });

    CuesheetCellActions.saveSelectedCellFormat(formatChange);
  },

  _saveFormatColor(type) {
    // shortcut to cycle through the top row of colors
    const colors = [
      'pink0',
      'green0',
      'yellow0',
      'orange0',
      'brown0',
      'red0',
      'black0',
      'blue0',
      'purple0',
    ];

    const format = this._getCellFormat();
    const fontFormat = format.get(type);
    const curColor = fontFormat ? fontFormat : colors[-1];
    let indx = colors.indexOf(curColor);

    let formatChange = Map({});

    indx += 1;

    if (!colors[indx]) {
      CuesheetCellActions.saveSelectedCellFormat(formatChange, type);
      return;
    }

    formatChange = formatChange.set(type, colors[indx]);

    CuesheetCellActions.saveSelectedCellFormat(formatChange);
  },

  _saveItemTimeFields(rowIndex, changes) {
    const state = this._getClearedEditState();

    this.cellObservables.saveItemFieldChangesDebounce.next({
      rowIndex,
      changes,
      state,
    });
  },

  onSaveItemNumber(rowIndex, number) {
    //Check if selected cell row is locked
    if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

    const changes = Map({ number });

    //Clear the edit state
    const state = this._getClearedEditState();

    this.cellObservables.saveItemFieldChangesDebounce.next({
      rowIndex,
      changes,
      state,
    });
  },

  _saveItemFieldChangesPersistent(itemSetId, rowId, changes, state) {
    state = state || this.getState();

    const rowIndex = this.findRowPosition(itemSetId, rowId, state);
    const rowSpec = this.getInState(['rows', rowIndex]);
    //If rowSpec is false indicates that there is no longer cuesheet state, so we just have to assume it changed
    if (rowSpec) {
      const rowSpec = this.getInState(['rows', rowIndex]);
      const rowPath = rowSpec.get('path');
      const existing = state.getIn(['rowMap'].concat(rowPath));
      const hasChanged = _valueHasChanged(existing, changes);
      if (!hasChanged) return;
    }

    CuesheetRowActions.updateItemRowPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      itemSetId,
      rowId,
      changes,
    );
  },

  onSaveMultipleDataCellChangesPersistentCompleted(res) {
    this.saveMultipleDataCellChanges(fromJS(res), false, true);
    ToastActions.saved();
  },

  onSaveMultipleDataCellChangesPersistentFailed(res) {
    this._revertFailedCells(res);
  },

  /**
   * Handle failed async result for updating an item row
   */
  onUpdateItemRowPersistentFailed(res) {
    this._revertFailedCells(res);
  },

  /**
   * Handle completed async result for updating an item row
   */
  onUpdateItemRowPersistentCompleted() {
    ToastActions.saved();
  },

  /**
   * Update item changes in UI state
   *
   * @param rowIndex number
   * @param changes Immutable Map
   * @param state immutable optional
   *
   */
  saveItemFieldChangesTransient(rowIndex, changes, state) {
    state = state || this.getState();

    const rowSpec = state.getIn(['rows', rowIndex]),
      rowPath = rowSpec.get('path');

    const transientChanges = fromJS(changes);

    //1. merge in the fields
    let next = state
      .updateIn(['rowMap'].concat(rowPath), (item) => {
        return _mergeCellChangesTransient(item, changes);
      })
      //2. 'touch' the row to force an update of the row
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime());

    if (
      transientChanges.has('rteTitleState') ||
      transientChanges.has('rteDescriptionState') ||
      transientChanges.has('actualDurationInSeconds')
    ) {
      CuesheetRowActions.bustRowHeightCache(rowPath[2]);
    }

    //3. Recalculate time fields
    /**
     * TODO: If auto time calc is off, we don't need to recalculate everything
     * we _could_ just update the 'regular' and 'adjusted' fields in state
     */
    if (
      transientChanges.has('startOn') ||
      transientChanges.has('presetDurationInSeconds') ||
      transientChanges.has('actualDurationInSeconds') ||
      transientChanges.has('durationInSeconds') ||
      transientChanges.has('durationSuppressed') ||
      transientChanges.has('displayPresetClock') ||
      transientChanges.has('number')
    ) {
      next = this.recalculateTimeFields(next);
    }

    return next;
  },

  /**
   * Update mutliple clock changes in UI state
   *
   * @param rowIndex number
   * @param changes Map
   * @param state immutable optional
   *
   */
  saveMultipleClockChangesTransient(rowIndex, changes, state) {
    state = state || this.getState();

    const rowSpec = this.getInState(['rows', rowIndex]),
      rowPath = rowSpec.get('path');

    let transientChanges = fromJS(changes);

    //1. merge in the fields
    const next = state
      .updateIn(['rowMap'].concat(rowPath), (item) => {
        const stateField = transientChanges.get('rteTitleState')
          ? 'rteTitleState'
          : 'rteDescriptionState';
        const textField = transientChanges.get('rteTitleState')
          ? 'title'
          : 'description';

        if (transientChanges && transientChanges.get(stateField)) {
          transientChanges = transientChanges.set(
            textField,
            getPlainTextFromRawRTEState(
              transientChanges.get(stateField).toJS(),
            ),
          );
        }

        return item.merge(transientChanges);
      })

      //2. 'touch' the row to force an update of the row
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime());

    this.setStateAndTrigger(next);

    return next;
  },

  saveDataCellChangesTransient(rowIndex, columnId, changes, initState) {
    const state = initState || this.getState();

    const rowSpec = this.getInState(['rows', rowIndex]),
      rowPath = rowSpec.get('path'),
      cellPath = ['rowMap'].concat(rowPath, 'cells', columnId);

    //Update the cell in next state
    const next = state
      .updateIn(cellPath, (cell) => {
        return _mergeCellChangesTransient(cell, changes, columnId);
      })
      //'touch' the row to force an update of the row
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime());

    return next;
  },

  /**
   * Save multiple cells at a single time.
   *
   * @param changes - example: {
   *    rowId: {
   *      columnId: {text: '', format: {}}
   *      columnId: {text: '', format: {}}
   *    },
   *    rowId: {
   *      columnId: {text: '', format: {}}
   *      columnId: {text: '', format: {}}
   *    }
   * }
   *
   * @param persistent
   * @param realtime
   * @param clearMode used when we are clearing cells
   *
   */
  saveMultipleDataCellChanges(
    changes,
    persistent,
    realtime,
    clearMode,
    params,
  ) {
    let next = realtime ? this.getState() : this._getClearedEditState();

    /**
     * In support of https://github.com/SHOFLO/Issues/issues/5202
     *
     * When we have unfilteredRows key in state, it means the Global Element view has a search or label filter, and
     * what is in the 'rows' key is a filtered set of rows. In order to get the row index to match up with the correct row
     * in realtime scenarios (sometimes the user receiving the realtime update has a different filter) we reset
     * 'rows' to what is in 'unfilteredRows'. We only do this for realtime & transient changes.
     *
     * Then further down we refilter the rows with any filters there are in state.
     */
    let needsRefilteringCleanup = false;
    if (!persistent && realtime && this.getInState('unfilteredRows')) {
      needsRefilteringCleanup = true;

      const rows = next.get('rows');
      const unfilteredRows = this.updateUnfilteredRowsWithRowFields(
        rows,
        next.get('unfilteredRows'),
      );

      next = next.set('unfilteredRows', unfilteredRows);
      next = next.set('rows', unfilteredRows);
    }

    params = params || this._getItemSetParams(next);

    const itemSetId = next.get('rowMap').keySeq().first();

    let hasPermission = true;

    const rowIdsCacheBust = [];

    changes.forEach((cells, rowId) => {
      rowIdsCacheBust.push(rowId);

      const rowIndex = this.findRowPosition(itemSetId, rowId, next),
        rowSpec = next.getIn(['rows', rowIndex]),
        rowPath = rowSpec.get('path');

      cells.forEach((cellData, columnId) => {
        if (!hasPermission) return;

        if (!Map.isMap(cellData)) cellData = Map(cellData);

        if (
          columnId === 'ItemStartTime' ||
          columnId === 'ItemPresetDuration' ||
          columnId === 'ItemDuration'
        ) {
          if (!realtime && !this.isEventAdmin()) {
            hasPermission = false;
          }

          const keys = cellData.keySeq().toArray();
          const path = ['rowMap'].concat(rowPath);

          keys.forEach((key) => {
            next = next.updateIn(path, (item) => {
              return item.set(key, cellData.get(key));
            });
          });
        } else if (columnId === 'ItemTitle' || columnId === 'ItemDescription') {
          /**
           * If they try to paste over title or description cells, and they aren't an event admin, refuse
           */
          if (!realtime && !this.isEventAdmin()) {
            hasPermission = false;
          }

          const path = ['rowMap'].concat(rowPath);
          const rteStateField =
            columnId === 'ItemTitle' ? 'rteTitleState' : 'rteDescriptionState';

          next = next.updateIn(path, (item) => {
            return _mergeCellChangesTransient(item, cellData, columnId);
          });

          //Update the changes going to server based on transient changes just made.
          //Only do this when the mutation itself carries content (rteState or the
          //title/description specific state): attaching this client's local copy of
          //the content to a content-less mutation (e.g. a format-only change) sends
          //a possibly stale snapshot that overwrites other users' concurrent edits
          //in the DB and reverts them on every screen.
          if (cellData.get('rteState') || cellData.get(rteStateField)) {
            changes = changes.setIn(
              [rowId, columnId, 'rteState'],
              next.getIn(path.concat(rteStateField)),
            );
            changes = changes.setIn(
              [rowId, columnId, rteStateField],
              next.getIn(path.concat(rteStateField)),
            );
          }
        } else if (columnId === 'ItemNote') {
          const cellPath = this._getNoteCellPath(
            rowIndex,
            cellData.get('userId'),
          );

          next = next.updateIn(cellPath, (cell) => {
            return _mergeCellChangesTransient(cell, cellData, columnId);
          });

          //Only normalize the outgoing payload when the mutation carries content;
          //see the ItemTitle/ItemDescription branch above for why
          if (cellData.get('rteState')) {
            changes = changes.setIn(
              [rowId, columnId, 'rteState'],
              next.getIn(cellPath.concat('rteState')),
            );
            changes = changes.setIn(
              [rowId, columnId, 'text'],
              getPlainTextFromRawRTEState(
                next.getIn(cellPath.concat('rteState')).toJS(),
              ),
            );
          }
        } else if (columnId === undefined) {
          //Means the column couldn't be found...which means the action was most likely executed on a cell that's a special column (but not ItemTitle or ItemDescription)
          //Deleting these from changes, was causing issue because we were sending up the edit with a value of undefined for the column, which
          //was causing 500s in the activity hook. In support of https://github.com/SHOFLO/Issues/issues/3791

          changes = changes.deleteIn([rowId, undefined]);
        } else {
          if (!realtime && !this.isEventContributor()) {
            hasPermission = false;
          }

          const cellPath = ['rowMap'].concat(rowPath, 'cells', columnId);

          // Update item cell state
          next = next.updateIn(cellPath, (cell) => {
            return _mergeCellChangesTransient(cell, cellData, columnId);
          });

          //Only normalize the outgoing payload when the mutation carries content;
          //see the ItemTitle/ItemDescription branch above for why
          if (cellData.get('rteState')) {
            const cellMutated = next.getIn(cellPath);

            changes = changes.setIn(
              [rowId, columnId, 'rteState'],
              cellMutated.get('rteState'),
            );
          }
        }
      });

      next = next.setIn(['rows', rowIndex, 'touch'], new Date().getTime());
    });

    if (!hasPermission) {
      return AlertActions.show(
        MessageConstants.pastePermissionError,
        UIRegions.Cuesheet,
      );
    }

    /**
     * In support of https://github.com/SHOFLO/Issues/issues/5202
     *
     * Refilter the rows in case they were changed above as part of a realtime transient change
     */
    if (needsRefilteringCleanup) {
      next = this._justFilterRows(next);
    }

    this.cellObservables.saveMultipleDataCellChangesBuffer.next({
      next,
      rowIdsCacheBust,
      persistent,
      changes,
      params,
    });
  },

  onClearPastableCellsBuffer() {
    this.setStateAndTrigger(this.clearPastableCellsBuffer());
  },

  _isHeaderCellSelected() {
    const selectedCell = this.getSelectedCell();
    let isHeaderCell = false;

    if (!selectedCell) {
      const selectedColumn = this._getSelectedColumn();

      if (selectedColumn) {
        isHeaderCell = true;
      }
    }

    return isHeaderCell;
  },

  /**
   * Handle Paste of cells data
   */
  onPasteCellsBuffer() {
    const nextState = this.getState(),
      selectedCell = this.getSelectedCell(),
      columnOrder = nextState.get('columnOrder');

    let startAtSelectedCell = this.getLastSelectedCell(),
      sortedRows = OrderedMap();

    /**
     * If user tries to paste in column header cell
     */
    if (this._isHeaderCellSelected()) {
      return AlertActions.show(
        MessageConstants.copyOrCutInHeaderCellWarning,
        UIRegions.Cuesheet,
      );
    }

    /**
     * If user tries to paste into a paste event
     */
    if (readOnlyHelper.alertPastEventNotEditable()) {
      return;
    }

    if (!selectedCell) return;

    if (!this.isMultiCellSelectable(startAtSelectedCell.cellSpec.get('type'))) {
      return AlertActions.show(
        MessageConstants.specialCellSelectionsModal,
        UIRegions.Cuesheet,
      );
    }

    /**
     * If there are not pastable cells then return.
     * If there isn't a cell selected then return.
     */
    if (
      !nextState.get('pastableCells') ||
      nextState.get('pastableCells').size < 1 ||
      !startAtSelectedCell
    )
      return;

    /**
     * Handle normal paste
     */
    if (nextState.get('pastableCells').size > 1) {
      nextState.get('pastableCells').forEach((cell) => {
        sortedRows = this._formatPastableRowCellData(
          sortedRows,
          cell,
          cell.data,
        );
      });
    } else if (nextState.get('pastableCells').size === 1) {
      /**
       * Handle pasting a single cell across a range of selected cells
       */
      startAtSelectedCell = this.getFirstSelectedCell();

      const cellData = nextState.get('pastableCells').first().data;

      nextState.get('selectedCells').forEach((cell) => {
        sortedRows = this._formatPastableRowCellData(
          sortedRows,
          cell,
          cellData,
        );
      });
    }

    /**
     * Sort rows
     */
    sortedRows = sortedRows.sortBy((row, rowId) => {
      let rowPos = this.findRowPosition(
        nextState.getIn(['pastableCellParams', 'itemSetId']),
        rowId,
      );

      // Use original row index when sorting and pasting rows across shows
      if (rowPos === -1) rowPos = row.getIn([0, 'originalRowIndex']);
      return rowPos;
    });

    /*
     * Sort cells in each row based on the column they are associated with inside
     * of the columnOrder field.
     */
    sortedRows.forEach((cellsArray, rowId) => {
      sortedRows = sortedRows.update(rowId, (cells) => {
        return cells.sortBy((cell) => {
          return nextState
            .get('columnOrder')
            .indexOf(cell.getIn(['cellSpec', 'value']));
        });
      });
    });

    /**
     * If columns and rows are not available to paste buffer throw error
     */
    const requiredRows = sortedRows.size;
    const requiredColumns = sortedRows.first().size;

    const startAtSelectedCellColumnIndex = columnOrder.indexOf(
      startAtSelectedCell.cellSpec.get('value'),
    );
    let availableColumns = columnOrder.size;
    if (startAtSelectedCellColumnIndex >= 0) {
      availableColumns -= startAtSelectedCellColumnIndex;
    } else {
      if (this.getInState(['show', 'displayItemTitles'])) {
        availableColumns++;
      }

      if (this.getInState(['show', 'displayItemDescriptions'])) {
        availableColumns++;
      }

      if (!this.getInState(['userSettings', 'noteColumn', 'hidden'])) {
        availableColumns++;
      }
    }

    const startAtSelectedCellRowIndex = startAtSelectedCell.rowIndex;
    const availableRows =
      nextState.get('rows').size - startAtSelectedCellRowIndex;

    if (availableColumns < requiredColumns || availableRows < requiredRows) {
      return AlertActions.show(
        MessageConstants.pasteShapeError,
        UIRegions.Cuesheet,
      );
    }

    /**
     * Check if any rows are locked
     *
     * TODO: Need to change this to selected cells to take into account paste a
     * range of cells
     */
    const checkRows =
      sortedRows.size > 1
        ? startAtSelectedCellRowIndex + (sortedRows.size - 1)
        : startAtSelectedCellRowIndex;

    for (
      let rowIndex = startAtSelectedCellRowIndex;
      rowIndex <= checkRows;
      rowIndex++
    ) {
      if (this.getItemRowLockStatus(rowIndex)) {
        return AlertActions.show(
          MessageConstants.lockedRowModal,
          UIRegions.Cuesheet,
        );
      }
    }

    const startAtCellSpecType = startAtSelectedCell.cellSpec.get('type');
    if (startAtCellSpecType === EditableCellTypes.ItemDuration) {
      return AlertActions.show(
        MessageConstants.pastingTimingCellsModal,
        UIRegions.Cuesheet,
      );
    } else if (startAtCellSpecType === EditableCellTypes.ItemPresetDuration) {
      return AlertActions.show(
        MessageConstants.pastingTimingCellsModal,
        UIRegions.Cuesheet,
      );
    } else if (startAtCellSpecType === EditableCellTypes.ItemStartTime) {
      return AlertActions.show(
        MessageConstants.pastingTimingCellsModal,
        UIRegions.Cuesheet,
      );
    }

    if (this.getInState('pasteCellsMode') === PasteModes.Cut) {
      this._cutCellData();
      this._pasteCellData(sortedRows, startAtSelectedCell, true);
    } else {
      this._pasteCellData(sortedRows, startAtSelectedCell, false);
    }
  },

  /**
   * Helper for constructring pastable rows
   */
  _formatPastableRowCellData(sortedRows, cell, cellData) {
    const { cellSpec } = cell;

    //Add row index to sorted rows if it does not exist.
    if (!sortedRows.get(cell.rowId))
      sortedRows = sortedRows.set(cell.rowId, List());

    //Add cellSpec to appropriate row index
    return sortedRows.update(cell.rowId, (row) => {
      return row.push(
        Map({
          cellSpec,
          data: cellData,
          originalRowIndex: cell.originalRowIndex,
        }),
      );
    });
  },

  /**
   * Removes the data from the cells that have been cut
   */
  _cutCellData() {
    const state = this.getState(),
      changes = state.get('cutCells'),
      pasteParams =
        state.get('pastableCellParams') || this._getItemSetParams(state);

    this.saveMultipleDataCellChanges(changes, true, false, true, pasteParams);
  },

  /**
   * Paste cell data
   *
   * @param rows Example: {
   *   rowIndex: {
   *     columnId: { data: {format, text}, cellSpec: {type, value}, rowIndex: number },
   *     columnId: { data: {format, text}, cellSpec: {type, value}, rowIndex: number },
   *   },
   *   rowIndex: ...
   * }
   */
  _pasteCellData(rows, startAtSelectedCell, clearPasteBuffer) {
    //Users column order. This is an array of column ids
    const columnOrder = this.getInState('columnOrder');

    /**
     * Row index of where to paste the cell.
     *
     * This selected cells row index will represent the upper left most cell's row.
     */
    let pasteRowIndex = startAtSelectedCell.rowIndex;

    /**
     * Selected column information of where to paste the cell.
     *
     * This column info will represent the upper left most cell's column of
     * where the cells will be pasted.
     */
    const pasteColumnId = startAtSelectedCell.cellSpec.get('value'),
      beginPasteColumnIndex = columnOrder.indexOf(pasteColumnId);

    let startPasteAtTitle = false,
      startPasteAtDesc = false,
      startPasteAtNote = false;

    if (
      startAtSelectedCell.cellSpec.get('type') === EditableCellTypes.ItemTitle
    ) {
      startPasteAtTitle = true;
    }
    if (
      startAtSelectedCell.cellSpec.get('type') ===
      EditableCellTypes.ItemDescription
    ) {
      startPasteAtDesc = true;
    }
    if (
      startAtSelectedCell.cellSpec.get('type') === EditableCellTypes.ItemNote
    ) {
      startPasteAtNote = true;
    }

    /**
     * If they try to paste over title or description cells, and they aren't an event admin, refuse
     */
    if ((startPasteAtTitle || startPasteAtDesc) && !this.isEventAdmin()) {
      return AlertActions.show(
        MessageConstants.pastePermissionError,
        UIRegions.Cuesheet,
      );
    }

    /**
     * Changes Example:
     * {
     *  <rowId>: {<columnId>: {text: '', format: {}}},
     *  <rowId>: {<columnId>: {text: '', format: {}}},
     * }
     */
    const displayItemDescriptions = this.getInState([
      'show',
      'displayItemDescriptions',
    ]);
    const hideNoteColumn = this.getInState([
      'userSettings',
      'noteColumn',
      'hidden',
    ]);
    const compact = isCompact();
    let changes = OrderedMap();
    changes = changes.withMutations((data) => {
      /**
       * Loop over each row from pasteBuffer
       */
      rows.map((cells) => {
        let pastedTitle = false;
        let pastedDesc = false;
        let pastedNote = false;

        const rowSpec = this.getInState(['rows', pasteRowIndex]),
          rowPath = rowSpec.get('path'),
          rowId = rowPath[2];

        data.set(rowId, OrderedMap());

        //Reset beginning paste column index for each row
        let pasteColumnIndex = beginPasteColumnIndex;

        cells.forEach((cell) => {
          if (startPasteAtTitle && !pastedTitle) {
            data.setIn(
              [rowId, 'ItemTitle'],
              cell
                .get('data')
                .update((d) => d.set('rteTitleState', d.get('rteState'))),
            );
            pastedTitle = true;
            if (!displayItemDescriptions || compact) {
              pasteColumnIndex = 0;
              pastedDesc = true;
            }

            if (hideNoteColumn) {
              pastedNote = true;
              pasteColumnIndex = 0;
            }
            return;
          }

          if ((startPasteAtDesc || startPasteAtTitle) && !pastedDesc) {
            pastedDesc = pastedTitle = true;
            pasteColumnIndex = 0;
            data.setIn(
              [rowId, 'ItemDescription'],
              cell
                .get('data')
                .update((d) => d.set('rteDescriptionState', d.get('rteState'))),
            );
            return;
          }

          if (
            (startPasteAtDesc || startPasteAtTitle || startPasteAtNote) &&
            !pastedNote
          ) {
            pastedDesc = pastedTitle = pastedNote = true;
            pasteColumnIndex = 0;
            data.setIn([rowId, 'ItemNote'], cell.get('data'));
            return;
          }
          data.setIn(
            [rowId, columnOrder.get(pasteColumnIndex)],
            cell.get('data'),
          );

          pasteColumnIndex++;
        });

        pasteRowIndex++;
      });

      return data;
    });

    if (clearPasteBuffer) {
      this.setStateAndTrigger(this.clearPastableCellsBuffer());
    }

    this.saveMultipleDataCellChanges(changes, true, false, false);
  },

  /**
   * Add selected cells to cell paste buffer
   */
  onAddSelectedCellsToPasteBuffer() {
    let nextState = this.getState();

    /**
     * If user tries to copy in column header cell
     */
    if (this._isHeaderCellSelected()) {
      return AlertActions.show(
        MessageConstants.copyOrCutInHeaderCellWarning,
        UIRegions.Cuesheet,
      );
    }

    if (!this.getSelectedCell()) return;

    if (this.specialCellSelections(nextState)) {
      return AlertActions.show(
        MessageConstants.specialCellSelectionsModal,
        UIRegions.Cuesheet,
      );
    }

    nextState = nextState.set('pasteCellsMode', PasteModes.Paste);
    nextState = this._addSelectedCellsToPastableBuffer(nextState);

    this.setStateAndTrigger(nextState);
  },

  /**
   * Add selected cells to cut buffer and paste buffer
   */
  onAddSelectedCellsToCutBuffer() {
    let nextState = this.getState();

    /**
     * If user tries to cut in column header cell
     */
    if (this._isHeaderCellSelected()) {
      return AlertActions.show(
        MessageConstants.copyOrCutInHeaderCellWarning,
        UIRegions.Cuesheet,
      );
    }

    if (!this.getSelectedCell()) return;

    /**
     * Return and throw alert if locked row is found amoung selections
     */
    if (this.lockedRowCellSelections(nextState)) {
      return AlertActions.show(
        MessageConstants.lockedRowModal,
        UIRegions.Cuesheet,
      );
    }

    if (this.specialCellSelections(nextState)) {
      return AlertActions.show(
        MessageConstants.specialCellSelectionsModal,
        UIRegions.Cuesheet,
      );
    }

    nextState = this._addSelectedCellsToPastableBuffer(nextState);
    nextState = this._addSelectedCellsToCutBuffer(nextState, true);

    this.setStateAndTrigger(nextState);
  },

  /**
   * Allows for things like cut and paste across shows
   */
  _addSelectedCellsToCutBuffer(state, clearFormat) {
    let nextState = state || this.getState();

    nextState = nextState.set('pasteCellsMode', PasteModes.Cut);

    let changes = OrderedMap();
    changes = changes.withMutations((data) => {
      /**
       * Loop over each selected cell
       */
      nextState.get('selectedCells').forEach((cell) => {
        const cellFormat = this.getCellFormat(cell);

        if (!data.get(cell.rowId)) data.set(cell.rowId, OrderedMap());
        const type = cell.cellSpec.get('type');
        let colId = cell.cellSpec.get('value');

        if (
          [
            EditableCellTypes.ItemNote,
            EditableCellTypes.ItemDescription,
            EditableCellTypes.ItemTitle,
            EditableCellTypes.ItemStartTime,
            EditableCellTypes.ItemPresetDuration,
            EditableCellTypes.ItemDuration,
          ].indexOf(type) >= 0
        ) {
          colId = type;
          //preprend Item onto the special column ID...this is the convention we have, although it could be made more
          //clear to use the same string representations of the enum EditableCellTypes (i.e. Note vs ItemNote)
          if (colId.indexOf('Item') === -1) colId = `Item${type}`;
        }

        let cellChanges;

        if (type === EditableCellTypes.ItemStartTime) {
          cellChanges = Map({
            displayStartTimeClock: false,
          });
        } else if (type === EditableCellTypes.ItemPresetDuration) {
          cellChanges = Map({
            displayPresetClock: false,
          });
        } else if (type === EditableCellTypes.ItemDuration) {
          cellChanges = Map({
            displayDurationClock: false,
            durationSuppressed: true,
            runTimeClockSuppressed: true,
          });
        } else {
          cellChanges = Map({
            rteState: getDefaultRTEState(),
            userId: nextState.get('userId'),
            format: cellFormat,
          });
          if (clearFormat) {
            //when cutting cells, this clears all formatting including cell background
            cellChanges = cellChanges.set('format', Map());
          } else if (cellFormat.has('backColor')) {
            cellChanges = cellChanges.set(
              'format',
              Map({ backColor: cellFormat.get('backColor') }),
            );
          }
        }

        data.setIn([cell.rowId, colId], cellChanges);
      });

      return data;
    });

    nextState = nextState.set('cutCells', changes);

    persistCopyState(nextState);

    return nextState;
  },

  _toggleTimeCellsVisibility(visible) {
    let nextState = this.getState(),
      lockedRowFound = false,
      noPermission = false;

    const itemSetId = nextState.get('rowMap').keySeq().first();

    nextState.get('selectedCells').forEach((selectedCell) => {
      const rowIndex = this.findRowPosition(itemSetId, selectedCell.rowId);
      //Check for locked row
      if (this.getItemRowLockStatus(rowIndex)) lockedRowFound = true;
      if (!this.isEventAdmin()) noPermission = true;
    });

    /**
     * Return and throw alert if locked row is found amoung selections
     */
    if (lockedRowFound)
      return AlertActions.show(
        MessageConstants.lockedRowModal,
        UIRegions.Cuesheet,
      );
    if (noPermission)
      return AlertActions.show(
        MessageConstants.lockedRowModal,
        UIRegions.Cuesheet,
      );

    let changes = OrderedMap();
    changes = changes.withMutations((data) => {
      /**
       * Loop over each selected cell
       */
      nextState.get('selectedCells').forEach((cell) => {
        if (!data.get(cell.rowId)) data.set(cell.rowId, OrderedMap());
        const type = cell.cellSpec.get('type');
        let colId = cell.cellSpec.get('value');

        if (
          [
            EditableCellTypes.ItemStartTime,
            EditableCellTypes.ItemPresetDuration,
            EditableCellTypes.ItemDuration,
          ].indexOf(type) >= 0
        ) {
          colId = type;
          //preprend Item onto the special column ID...this is the convention we have, although it could be made more
          //clear to use the same string representations of the enum EditableCellTypes (i.e. Note vs ItemNote)
          if (colId.indexOf('Item') === -1) colId = `Item${type}`;
        }

        let cellChanges;

        const rowIndex = this.findRowPosition(itemSetId, cell.rowId);
        const path = nextState.getIn(['rows', rowIndex, 'path']);
        let item = nextState.getIn(['rowMap'].concat(path));

        if (type === EditableCellTypes.ItemStartTime) {
          cellChanges = Map({
            displayStartTimeClock: visible,
          });
          item = item.set('displayStartTimeClock', visible);
        } else if (type === EditableCellTypes.ItemPresetDuration) {
          cellChanges = Map({
            displayPresetClock: visible,
          });
          item = item.set('displayPresetClock', visible);
        } else if (type === EditableCellTypes.ItemDuration) {
          cellChanges = Map({
            displayDurationClock: visible,
            durationSuppressed: !visible,
            runTimeClockSuppressed: !visible,
          });
          item = item
            .set('displayDurationClock', visible)
            .set('durationSuppressed', !visible)
            .set('runTimeClockSuppressed', !visible);
        }

        nextState = nextState.setIn(['rowMap'].concat(path), fromJS(item));

        data.setIn([cell.rowId, colId], cellChanges);
      });

      return data;
    });

    this.recalculateTimeFieldsAndTrigger(nextState);

    this.saveMultipleDataCellChanges(changes, true, false, true);
  },

  _getItemSetParams(state) {
    const itemSetId = state.get('rowMap').keySeq().first();

    return Map({
      teamId: state.get('teamId'),
      eventId: state.get('eventId'),
      showId: state.get('showId'),
      itemSetListId: state.get('itemSetListId'),
      itemSetId,
    });
  },

  _addSelectedCellsToPastableBuffer(state) {
    let nextState = state || this.getState();

    //If there are no selected cells there is nothing to add to the paste buffer
    if (nextState.get('selectedCells').size < 1) return nextState;

    const itemSetId = nextState.get('rowMap').keySeq().first();

    /**
     * pasteableCellParams allows us to paste accross shows and have the
     * required params to clear the cells from the previous show the cells were
     * cut from.
     */
    nextState = nextState.set(
      'pastableCellParams',
      this._getItemSetParams(nextState),
    );

    nextState = nextState.withMutations((state) => {
      //Clear old pastable cell states
      state = this.clearPastableCellState(state);

      if (this.selectedCellsFormValidRectangle()) {
        state.set('pastableCells', OrderedMap());

        state.get('selectedCells').forEach((cell, key) => {
          const { rowId, cellSpec } = cell,
            rowIndex = this.findRowPosition(itemSetId, rowId);

          /**
           * Add data from cell to pastableCells buffer.
           *
           * This is a duplicate of data but allows the data to persist and
           * be pasted across shows, schedules, and lists.
           */
          cell.data = this._getPastableCellData(rowIndex, cellSpec);
          cell.originalRowIndex = rowIndex;

          state.setIn(['pastableCells', key], cell);
          this.touchRow(rowIndex, state);
        });

        return state;
      }

      return AlertActions.show(
        'Invalid command with current selection pattern.',
        UIRegions.Cuesheet,
      );
    });

    persistCopyState(nextState);

    return nextState;
  },

  /**
   * Get pastable cell data
   */
  _getPastableCellData(rowIndex, cellSpec) {
    const state = this.getState(),
      path = state.getIn(['rows', rowIndex, 'path']),
      item = state.getIn(['rowMap'].concat(path)),
      cellType = cellSpec.get('type'),
      userId = state.get('userId'),
      cell = item.getIn(['cells', cellSpec.get('value')]);

    let rteState =
        cell && cell.has('rteState')
          ? cell.get('rteState')
          : getDefaultRTEState(),
      format = cell && cell.get('format') ? cell.get('format') : {};

    if (cellType === EditableCellTypes.ItemTitle) {
      rteState = item.get('rteTitleState')
        ? item.get('rteTitleState')
        : getDefaultRTEState();
      format = item.get('titleFormat') ? item.get('titleFormat') : {};
    } else if (cellType === EditableCellTypes.ItemDescription) {
      rteState = item.get('rteDescriptionState')
        ? item.get('rteDescriptionState')
        : getDefaultRTEState();
      format = item.get('descriptionFormat')
        ? item.get('descriptionFormat')
        : {};
    } else if (cellType === EditableCellTypes.ItemNote) {
      const note = item.getIn(['userSettings', userId, 'note']);

      if (note) {
        rteState = note.has('rteState')
          ? note.get('rteState')
          : getDefaultRTEState();
        format = note.has('format') ? note.get('format') : {};
      }
    }

    return fromJS({ rteState, format, userId });
  },

  /**
   * Clear pastable cell state.
   */
  clearPastableCellState(state) {
    state = state || this.getState();

    if (!state.get('pastableCells') || state.get('pastableCells').size < 1)
      return state;

    state = state.withMutations((nextState) => {
      nextState = this.touchAllPastableCellRows(nextState);
      nextState.delete('pastableCells');

      return nextState;
    });

    return state;
  },

  _startEditingRow(rowIndex, cellSpec, state) {
    const cellSpecMap = Map(cellSpec);

    if (this.getEditableCellStatusInLockedRowWithAlert(rowIndex, cellSpecMap))
      return state;

    CuesheetRowActions.clearRowSelection();

    const nextState = this._getClearedEditState(
      this.clearPastableCellsBuffer(state),
    )
      .setIn(['rows', rowIndex, 'editing'], cellSpecMap)
      .set('keyScope', KeyScopes.CellEditing)
      .set('scrollColumnIndex', this._getCellIndex(cellSpecMap) + 1);

    this.setStateAndTrigger(nextState);

    return nextState;
  },

  _startEditingHeaderCell(columnId) {
    const cellSpec = Map({ type: EditableCellTypes.Data, value: columnId });
    const next = this._getClearedEditState()
      .setIn(['columns', columnId, 'editing'], true)
      .set('keyScope', KeyScopes.CellEditing)
      .set('scrollColumnIndex', this._getCellIndex(cellSpec) + 1);

    this.setStateAndTrigger(this.clearPastableCellsBuffer(next));
  },

  _rowIsCorrupted(rowIndex) {
    const corruptedTitle = PlaceholderItem.get('title');

    const row = this.getInState(['rows', rowIndex]);
    const rowPath = row.get('path');
    const rowData = this.getInState(['rowMap'].concat(rowPath));

    return rowData.get('title') === corruptedTitle;
  },

  _getSelectedColumn() {
    return this.getInState('columns').find((col) => col.get('selected'));
  },

  _getClearedEditState(state) {
    state = this.clearRowFieldState('editing', state).set(
      'keyScope',
      KeyScopes.Default,
    );

    state = this.clearColumnFieldState('editing', state);

    return state;
  },

  clearPastableCellsBuffer(state) {
    let next = state || this.getState();

    next = this.touchAllPastableCellRows(next);

    return next
      .delete('pasteCellsMode')
      .delete('pastableCells')
      .delete('pastableCellParams')
      .delete('cutCells');
  },

  _hasCellPermission(cellType) {
    if (cellType !== EditableCellTypes.ItemNote && !this.isEventContributor())
      return false;
    if (
      cellType !== EditableCellTypes.ItemNote &&
      cellType !== EditableCellTypes.Data &&
      !this.isEventAdmin()
    )
      return false;

    return true;
  },

  _isFormattable(cellType) {
    if (cellType === EditableCellTypes.ItemNote) return true;

    if (
      this.isEventAdmin() &&
      (cellType === EditableCellTypes.ItemTitle ||
        cellType === EditableCellTypes.ItemDescription ||
        cellType === EditableCellTypes.Data)
    )
      return true;

    if (this.isEventContributor() && cellType === EditableCellTypes.Data)
      return true;

    return false;
  },

  _setToolbarVisibility(type) {
    this.setInState('cellToolbarEnabled', this._isFormattable(type));
  },

  /**
   * A cell/item-field save failed, so the optimistic edit is now unsaved.
   * Revert it by refetching the affected rows' authoritative values from the DB.
   * Refetching (rather than restoring a client snapshot) is what makes
   * overlapping saves safe: the DB reflects exactly which of the concurrent
   * saves landed, so a later save to the same row that succeeded is preserved
   * while the failed one is rolled back.
   */
  _revertFailedCells(res) {
    //Report the save failure immediately; don't claim a revert yet — that's only
    //true once the refetch below succeeds (the visible cell reverting to its DB
    //value is the confirmation).
    ToastActions.failure('Some changes failed to save.', 4000);

    const rowIds = this._getFailedRowIds(res);
    if (!rowIds.length) return;

    return ItemSetListData.getSubset(
      this.getInState('teamId'),
      this.getInState('eventId'),
      this.getInState('showId'),
      null,
      rowIds,
    ).then(
      (itemSetList) => this._applyRefetchedRows(itemSetList),
      (err) => {
        //The refetch that would revert the optimistic edit failed too, so the
        //unsaved value is still on screen. Surface it rather than swallowing —
        //the local state can't be trusted until a reload re-syncs with the DB.
        console.error('Failed to refetch cells after failed save', err);
        ToastActions.failure(
          'Could not restore the last saved version. Please reload.',
          5000,
        );
      },
    );
  },

  /**
   * Pull the affected rowIds out of a failed request's payload.
   *
   * updateMultipleItemRowCells sends { itemSetId, changes } where `changes` is
   * keyed by rowId. Single-row updates (updateItemRow) send { itemSetId, rowId,
   * changes } where `changes` is keyed by column — so the rowId comes from
   * payload.rowId, and its `changes` keys must NOT be treated as rowIds.
   */
  _getFailedRowIds(res) {
    const config = res && res.config;
    if (!config) return [];

    let data = config.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return [];
      }
    }

    const payload = data && data.payload;
    if (!payload) return [];

    if (payload.rowId) return [payload.rowId];
    if (payload.changes) return Object.keys(payload.changes);

    return [];
  },

  /**
   * Replace the affected rows' item data in the rowMap with the authoritative
   * values just fetched from the DB, using the same row->item builder as the
   * initial load (CuesheetDataHelper.buildRowItem / createNestedMap). Replacing
   * the whole item (rather than merging) ensures a cell the edit added is
   * cleared when the DB has no value for it.
   */
  _applyRefetchedRows(itemSetList) {
    const itemSet =
      itemSetList && itemSetList.itemSets && itemSetList.itemSets[0];
    if (!itemSet || !itemSet.rows || !itemSet.rows.length) return;

    const itemSetId = itemSet._id;
    const revertedRowIds = [];

    const next = this.getState().withMutations((state) => {
      itemSet.rows.forEach((row) => {
        const rowId = row._id;
        const itemPath = ['rowMap'].concat(
          CuesheetDataHelper.getRowPath(itemSetId, rowId),
        );

        //Skip rows this client doesn't have loaded (e.g. filtered out).
        if (state.getIn(itemPath) === undefined) return;

        let nextItem = fromJS(CuesheetDataHelper.buildRowItem(row));

        //The subset fetch doesn't carry the globalElement marker, and it isn't
        //part of the saved cell data we're reverting, so keep the existing one.
        const globalElement = state.getIn(itemPath.concat('globalElement'));
        if (globalElement !== undefined) {
          nextItem = nextItem.set('globalElement', globalElement);
        }

        state.setIn(itemPath, nextItem);
        revertedRowIds.push(rowId);
      });
    });

    if (!revertedRowIds.length) return;

    CuesheetRowActions.bustRowHeightCache(revertedRowIds);
    this.recalculateTimeFieldsAndTrigger(next);
  },
};

/**
 * Compare two immutable iterable values to see if any values in the changed map have changed in the existing map
 *
 * @param existing
 * @param changes
 * @returns {boolean}
 * @private
 */
export function _rteHasChanged(existing, changes) {
  let hasChanged = false;

  // if existing is falsey, then changes have occurred
  if (!existing) return true;

  // existingData from an empty cell has placeholders in its entityMap that make it nonequal to an empty rteState in
  // changes despite no practical change. to get around this, we compare the text contents of each instead and return
  // hasChanged = false if both have empty text values
  if (
    existing.get('text') === '' &&
    changes.get('blocks') &&
    changes.get('blocks').size === 1 &&
    changes.getIn(['blocks', 0, 'text']) === ''
  ) {
    return hasChanged;
  }

  let existingRTEState = existing.get('rteState');

  // if there's no existing rteState to compare against, treat it as changed
  if (!existingRTEState) return true;

  // all new rte states have at least an empty object within the data field. if the existing
  // rteState doesn't have a data field, we add it so we can compare the two
  if (!existingRTEState.hasIn(['blocks', '0', 'data'])) {
    existingRTEState = existingRTEState.setIn(['blocks', '0', 'data'], Map({}));
  }

  const equalText =
    getPlainTextFromRawRTEState(changes.toJS()) ===
    getPlainTextFromRawRTEState(existingRTEState.toJS());

  if (!equalText) hasChanged = true;

  // some existing cells have placeholders in their entityMap that make them nonequal to an empty entityMap in changes
  // despite having the same text and styles and no relevant entities. this removes the placeholder so they can be
  // compared
  if (existingRTEState && typeof existingRTEState === 'object') {
    const rteContentState = _getRteContentState(existingRTEState);

    const entityType = existingRTEState.hasIn(['entityMap', '0', 'type'])
      ? existingRTEState.get('entityMap').get('0').get('type')
      : null;

    if (entityType === 'PLACEHOLDER') {
      existingRTEState = fromJS(
        convertToRaw(
          Modifier.applyEntity(
            rteContentState,
            _getEntireEditorSelection(rteContentState),
            null,
          ),
        ),
      );
    }
  }

  changes.forEach((val, key) => {
    const existingVal = existingRTEState.get(key);
    // if the existing rteState lacks a top-level key present in changes, it's changed
    if (!existingVal) {
      hasChanged = true;
      return;
    }
    val.forEach((newData, nestedKey) => {
      const existingData = existingVal.get(nestedKey);
      if (existingData) {
        if (!newData.equals(existingData)) hasChanged = true;
      } else {
        hasChanged = true;
      }
    });
  });

  return hasChanged;
}

/**
 * Compare two immutable iterable values to see if any values in the changed map have changed in the existing map
 *
 * @param existing
 * @param changes
 * @returns {boolean}
 * @private
 */
export function _valueHasChanged(existing, changes) {
  let hasChanged = false;

  if (!existing && !changes) return hasChanged;

  // if existing is falsey, then changes have occurred
  if (!existing) return true;

  const iterableChanges = Map.isMap(changes) ? changes.toJS() : changes;

  _.each(iterableChanges, (val, key) => {
    val = Map.isMap(val) ? val : fromJS(val);
    const existingData = existing.get(key);

    if (
      existingData &&
      typeof existingData.equals === 'function' &&
      !existingData.equals(val)
    ) {
      hasChanged = true;
    } else if (!_.isEqual(val, existingData)) {
      hasChanged = true;
    }
  });

  return hasChanged;
}

/**
 * Merge cell changes transient (apply formatting and text fields)
 *
 * @param cell
 * @param changes
 * @returns {*}
 * @private
 */
const _mergeCellChangesTransient = (cell, changes, columnId) => {
  //There will be no cell for notes until an initial edit is made
  if (!cell) cell = Map();

  let transientChanges = fromJS(changes);

  if (
    columnId === EditableCellTypes.ItemTitle ||
    columnId === EditableCellTypes.ItemDescription
  ) {
    const stateField =
      columnId === 'ItemTitle' ? 'rteTitleState' : 'rteDescriptionState';
    const textField = columnId === 'ItemTitle' ? 'title' : 'description';

    const cellState = transientChanges.get(stateField)
      ? transientChanges.get(stateField)
      : transientChanges.get('rteState');

    if (cellState) {
      cell = cell
        .set('rteState', cellState)
        .set(stateField, cellState)
        .set(textField, getPlainTextFromRawRTEState(cellState.toJS()));
    }

    if (transientChanges.get('format')) {
      cell = cell.set(`${textField}Format`, changes.get('format'));
      transientChanges = transientChanges.delete('format');
    }
  } else {
    if (changes.get('format')) {
      cell = cell.set('format', changes.get('format'));
    }
    if (transientChanges.get('rteState')) {
      cell = cell
        .set('rteState', transientChanges.get('rteState'))
        .set(
          'text',
          getPlainTextFromRawRTEState(transientChanges.get('rteState').toJS()),
        );
    }
  }

  cell = cell.merge(transientChanges);

  return cell;
};

const _applyCellFormatsToRTEState = (
  cellFormat,
  additions,
  removals,
  rteState,
) => {
  let rteWithCellFormats = rteState;

  const addStyles = [];
  const removeStyles = [];

  if (additions.has('bold')) {
    if (additions.get('bold')) {
      addStyles.push('BOLD');
    } else removeStyles.push('BOLD');
  }

  if (additions.has('italic')) {
    if (additions.get('italic')) {
      addStyles.push('ITALIC');
    } else removeStyles.push('ITALIC');
  }

  if (additions.has('uppercase')) {
    if (additions.get('uppercase')) {
      addStyles.push('UPPERCASE');
    } else removeStyles.push('UPPERCASE');
  }

  if (additions.has('fontSize')) {
    const fontSizeName = additions.get('fontSize');
    const fontSize = FontSizeMap[fontSizeName];
    addStyles.push(`fontsize-${fontSize}`);
    //if there is already a value for this style, we remove it else it overrides the new style
    if (cellFormat.has('fontSize')) {
      const existingFontSizeName = cellFormat.get('fontSize');
      const existingFontSize = FontSizeMap[existingFontSizeName];
      removeStyles.push(`fontsize-${existingFontSize}`);
    }
  }

  if (additions.has('foreColor')) {
    addStyles.push(`color-${additions.get('foreColor')}`);
    //if there is already a value for this style, we remove it overrides the new style
    if (cellFormat.has('foreColor'))
      removeStyles.push(`color-${cellFormat.get('foreColor')}`);
  }

  if (removals.has('foreColor')) {
    removeStyles.push(`color-${cellFormat.get('foreColor')}`);
  }

  if (additions.has('align')) {
    const align = additions.get('align');
    rteWithCellFormats = _applyAlignmentToState(rteWithCellFormats, align);
  }

  removeStyles.forEach((style) => {
    rteWithCellFormats = _applyStyleToState(rteWithCellFormats, style, true);
  });

  addStyles.forEach((style) => {
    rteWithCellFormats = _applyStyleToState(rteWithCellFormats, style);
  });

  return rteWithCellFormats;
};

const _applyAlignmentToState = (rawState, alignment) => {
  const state = _getRteContentState(rawState);

  return fromJS(
    convertToRaw(
      Modifier.setBlockData(state, _getEntireEditorSelection(state), {
        'text-align': alignment,
      }),
    ),
  );
};

const _applyStyleToState = (rawState, style, remove) => {
  const state = _getRteContentState(rawState);

  const modFunc = remove ? 'removeInlineStyle' : 'applyInlineStyle';

  return fromJS(
    convertToRaw(
      Modifier[modFunc](state, _getEntireEditorSelection(state), style),
    ),
  );
};

const _getRteContentState = (rawState) =>
  rawState
    ? convertFromRaw(rawState.toJS())
    : EditorState.createEmpty().getCurrentContent();

const _getEntireEditorSelection = (state) => {
  return new SelectionState({
    anchorOffset: 0,
    focusOffset: state.getLastBlock().getLength(),
    hasFocus: false,
    anchorKey: state.getFirstBlock().getKey(),
    focusKey: state.getLastBlock().getKey(),
  });
};
